import type { CarromGame, Disc } from "./types.js";
import { BOARD_W, BOARD_H, DISC_R, STRIKER_R, CENTER, POCKETS, POCKET_R } from "./types.js";

function initDiscs(): Disc[] {
  const discs: Disc[] = [];
  const positions = [
    { x: CENTER.x, y: CENTER.y, color: "red" as const },
    { x: CENTER.x - 24, y: CENTER.y, color: "black" as const },
    { x: CENTER.x + 24, y: CENTER.y, color: "white" as const },
    { x: CENTER.x - 12, y: CENTER.y - 20, color: "black" as const },
    { x: CENTER.x + 12, y: CENTER.y - 20, color: "white" as const },
    { x: CENTER.x - 12, y: CENTER.y + 20, color: "white" as const },
    { x: CENTER.x + 12, y: CENTER.y + 20, color: "black" as const },
    { x: CENTER.x, y: CENTER.y - 40, color: "white" as const },
    { x: CENTER.x, y: CENTER.y + 40, color: "black" as const },
    { x: CENTER.x - 36, y: CENTER.y - 20, color: "white" as const },
    { x: CENTER.x + 36, y: CENTER.y - 20, color: "black" as const },
    { x: CENTER.x - 36, y: CENTER.y + 20, color: "white" as const },
    { x: CENTER.x + 36, y: CENTER.y + 20, color: "black" as const },
    { x: CENTER.x - 24, y: CENTER.y - 40, color: "black" as const },
    { x: CENTER.x + 24, y: CENTER.y - 40, color: "white" as const },
    { x: CENTER.x - 24, y: CENTER.y + 40, color: "white" as const },
    { x: CENTER.x + 24, y: CENTER.y + 40, color: "black" as const },
    { x: CENTER.x, y: CENTER.y - 60, color: "black" as const },
    { x: CENTER.x, y: CENTER.y + 60, color: "white" as const },
  ];
  for (const pos of positions) {
    discs.push({ ...pos, vx: 0, vy: 0, pocketed: false, r: DISC_R });
  }
  return discs;
}

export function createGame(): CarromGame {
  return {
    discs: initDiscs(),
    striker: { x: BOARD_W / 2, y: BOARD_H - 40, vx: 0, vy: 0, color: "striker", pocketed: false, r: STRIKER_R },
    turn: 1,
    scores: [0, 0],
    aimAngle: -90,
    power: 30,
    inMotion: false,
    winner: null,
    message: "Player 1's turn. Aim and shoot!",
  };
}

export function setAim(game: CarromGame, angle: number): CarromGame {
  if (game.inMotion) return game;
  return { ...game, aimAngle: Math.max(-180, Math.min(0, angle)) };
}

export function setPower(game: CarromGame, power: number): CarromGame {
  if (game.inMotion) return game;
  return { ...game, power: Math.max(5, Math.min(100, power)) };
}

export function shoot(game: CarromGame): CarromGame {
  if (game.inMotion) return game;
  const angleRad = (game.aimAngle * Math.PI) / 180;
  const speed = game.power * 2;
  return {
    ...game,
    striker: { ...game.striker, vx: Math.cos(angleRad) * speed, vy: Math.sin(angleRad) * speed },
    inMotion: true,
  };
}

function isInPocket(d: Disc): { pocketed: boolean; pocketX: number; pocketY: number } | null {
  for (const p of POCKETS) {
    const dx = d.x - p.x;
    const dy = d.y - p.y;
    if (Math.sqrt(dx * dx + dy * dy) < POCKET_R) {
      return { pocketed: true, pocketX: p.x, pocketY: p.y };
    }
  }
  return null;
}

function resolveCollision(a: Disc, b: Disc): { a: Disc; b: Disc } {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dist = Math.sqrt(dx * dx + dy * dy);
  if (dist === 0) return { a, b };
  const overlap = a.r + b.r - dist;
  if (overlap <= 0) return { a, b };

  const nx = dx / dist;
  const ny = dy / dist;

  const dvx = a.vx - b.vx;
  const dvy = a.vy - b.vy;
  const dvn = dvx * nx + dvy * ny;

  if (dvn > 0) return { a, b };

  const ma = 1, mb = 1;
  const impulse = (2 * dvn) / (ma + mb);

  return {
    a: { ...a, vx: a.vx - impulse * mb * nx, vy: a.vy - impulse * mb * ny },
    b: { ...b, vx: b.vx + impulse * ma * nx, vy: b.vy + impulse * ma * ny },
  };
}

export function tick(game: CarromGame, dt: number): CarromGame {
  if (!game.inMotion) return game;
  let dtSec = dt / 1000;
  if (dtSec > 0.05) dtSec = 0.05;

  const discs = game.discs.map((d) => ({
    ...d, vx: d.vx * 0.98, vy: d.vy * 0.98,
    x: d.x + d.vx * dtSec, y: d.y + d.vy * dtSec,
  }));
  let striker = {
    ...game.striker, vx: game.striker.vx * 0.98, vy: game.striker.vy * 0.98,
    x: game.striker.x + game.striker.vx * dtSec,
    y: game.striker.y + game.striker.vy * dtSec,
  };

  for (const d of discs) {
    if (d.pocketed) continue;
    if (d.x - d.r < 10) { d.x = 10 + d.r; d.vx = Math.abs(d.vx) * -0.5; }
    if (d.x + d.r > BOARD_W - 10) { d.x = BOARD_W - 10 - d.r; d.vx = Math.abs(d.vx) * 0.5; }
    if (d.y - d.r < 10) { d.y = 10 + d.r; d.vy = Math.abs(d.vy) * -0.5; }
    if (d.y + d.r > BOARD_H - 10) { d.y = BOARD_H - 10 - d.r; d.vy = Math.abs(d.vy) * 0.5; }

    const pocket = isInPocket(d);
    if (pocket) {
      d.pocketed = true;
      d.vx = 0; d.vy = 0;
    }
  }

  const allDiscs = [striker, ...discs];
  for (let i = 0; i < allDiscs.length; i++) {
    for (let j = i + 1; j < allDiscs.length; j++) {
      if (allDiscs[i].pocketed || allDiscs[j].pocketed) continue;
      const result = resolveCollision(allDiscs[i], allDiscs[j]);
      allDiscs[i] = result.a;
      allDiscs[j] = result.b;
    }
  }

  striker = allDiscs[0];
  const updatedDiscs = allDiscs.slice(1) as Disc[];

  const pocket = isInPocket(striker);
  if (pocket) {
    striker.pocketed = true;
    striker.vx = 0; striker.vy = 0;
  }

  const anyMotion = allDiscs.some((d) => !d.pocketed && (Math.abs(d.vx) > 0.5 || Math.abs(d.vy) > 0.5));

  if (!anyMotion) {
    let pocketedWhite = 0, pocketedBlack = 0, pocketedRed = false;
    for (const d of updatedDiscs) {
      if (!d.pocketed) continue;
      if (d.color === "white") pocketedWhite++;
      if (d.color === "black") pocketedBlack++;
      if (d.color === "red") pocketedRed = true;
    }

    const scores: [number, number] = [...game.scores];
    if (game.turn === 1) scores[0] += pocketedWhite * 1 + (pocketedRed ? 3 : 0);
    else scores[1] += pocketedBlack * 1 + (pocketedRed ? 3 : 0);

    const remaining = updatedDiscs.filter((d) => !d.pocketed);
    const nextTurn: 1 | 2 = game.turn === 1 ? 2 : 1;
    let winner: number | null = null;

    const whiteLeft = remaining.filter((d) => d.color === "white").length;
    const blackLeft = remaining.filter((d) => d.color === "black").length;

    if (whiteLeft === 0 && blackLeft === 0) {
      winner = scores[0] > scores[1] ? 1 : scores[1] > scores[0] ? 2 : null;
    }

    return {
      ...game,
      discs: updatedDiscs,
      striker: { ...striker, x: BOARD_W / 2, y: BOARD_H - 40, vx: 0, vy: 0, pocketed: false },
      inMotion: false,
      scores,
      turn: nextTurn,
      winner,
      message: winner ? `Player ${winner} wins!` : `Player ${nextTurn}'s turn`,
    };
  }

  return { ...game, discs: updatedDiscs, striker, inMotion: true };
}
